import React from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import { cn } from "../../lib/utils";

// Conditional smart fields displayed on the public lead form based on the
// selected service type. Each service maps to the canonical service_type
// `value` strings used in PublicLeadForm.jsx (SERVICE_TYPES) so they share
// a single source of truth.
//
// IMPORTANT — input focus stability:
//   Earlier revisions of this component defined small inner helper
//   components (YesNo, Enum, NumberInput) inside the parent function
//   body. On every parent render React created NEW component identities,
//   which forced the underlying <input>/<Select> to unmount and remount on
//   each keystroke. That manifested as "cursor disappears / value resets /
//   can't delete" — the classic controlled-input remount bug.
//   This version inlines the JSX per case so React always sees the same
//   component tree across renders, preserving focus while typing.

const SHEETS = {
  "Standard Clean": ["bedrooms", "bathrooms", "square_footage", "frequency", "pets"],
  "Deep Clean":     ["bedrooms", "bathrooms", "square_footage", "home_condition", "pets"],
  "Move-Out":       ["bedrooms", "bathrooms", "square_footage", "property_empty", "inside_appliances"],
  "Move-In":        ["bedrooms", "bathrooms", "square_footage", "property_empty", "inside_appliances"],
  "Post-Construction": ["square_footage", "construction_type", "heavy_dust", "debris_removal"],
  "Airbnb Turnover":   ["bedrooms", "bathrooms", "square_footage", "laundry_needed", "restocking_needed"],
  "Office / Commercial": ["square_footage", "restrooms", "frequency", "business_type"],
  // Bathroom and Kitchen "only" sheets are wired for future use; the
  // PublicLeadForm SERVICE_TYPES list doesn't expose them today.
  "Bathroom Cleaning": ["num_bathrooms", "shower_tub", "hard_water_grout"],
  "Kitchen Cleaning":  ["num_kitchens", "oven_inside", "fridge_inside", "cabinets_inside"],
};

// Some sheet keys are display-only aliases that resolve to a different
// stored key in `serviceDetails` (e.g. `num_bathrooms` is rendered as a
// distinct sheet field but written under the canonical `bathrooms` key so
// the downstream AI sees one consistent name). Validation must work on
// the stored key, so the public form uses `getRequiredServiceFieldKeys`
// to project a sheet's field list onto the keys actually persisted in
// `serviceDetails`. Any new alias added here automatically flows through
// to validation — required-field enforcement is fully generic and does
// not need to be updated per-field.
const FIELD_KEY_ALIAS = {
  num_bathrooms: "bathrooms",
};

// Return the de-duplicated list of `serviceDetails` keys that MUST be
// filled when the given service type is selected. Returns [] when no
// service type is selected or the type has no smart-fields sheet.
export function getRequiredServiceFieldKeys(serviceType) {
  const fields = SHEETS[serviceType] || [];
  const mapped = fields.map((k) => FIELD_KEY_ALIAS[k] || k);
  return Array.from(new Set(mapped));
}

const YES_NO_OPTIONS = ["yes", "no"];

// Frequency vocabulary differs slightly between residential and
// commercial: residential rarely books daily cleans.
const RES_FREQ = ["one_time", "weekly", "biweekly", "monthly"];
const COM_FREQ = ["one_time", "daily", "weekly", "biweekly", "monthly"];

const CONDITION_OPTIONS = ["light", "average", "heavy"];
const BIZ_OPTIONS = ["office", "retail", "medical", "restaurant", "other"];
const CONSTR_OPTIONS = ["renovation", "new_build", "remodel", "other"];

// Set field-level helpers. Both `setBool` and `setEnum` and `setNumber`
// are STABLE closures created at the top of the component using the
// `setDetails` setter — they don't need to be memoised because the
// component itself receives a stable setter from the parent (useState
// setter identity is stable across renders).
export function ServiceDetailsFields({ serviceType, details, setDetails, t, errors = {} }) {
  const fields = SHEETS[serviceType] || [];
  if (fields.length === 0) return null;

  // Generic value setter. Pass `undefined` to clear.
  const setKey = (k, v) =>
    setDetails((d) => {
      const next = { ...d };
      if (v === undefined || v === "" || v === null) {
        delete next[k];
      } else {
        next[k] = v;
      }
      return next;
    });

  // Resolve the error message for a given stored-key (e.g. "bathrooms",
  // not "num_bathrooms"). PublicLeadForm always keys its errors object by
  // the same `sd.<stored_key>` shape, so this stays in lock-step with the
  // alias map (`FIELD_KEY_ALIAS`) used for validation.
  const errFor = (storedKey) => errors[`sd.${storedKey}`];

  // Render helpers as plain JSX builders (not components). Stable across
  // renders because they're called inline during the parent render.
  //
  // Every smart-field rendered here is REQUIRED before the public lead
  // form can be submitted (validation is enforced generically in
  // PublicLeadForm via `getRequiredServiceFieldKeys`). The unconditional
  // red asterisk in the label is the visible counterpart to that rule,
  // so any future service type / new field automatically inherits the
  // "required" treatment without any per-field bookkeeping here.
  const renderLabel = (label, htmlFor) => (
    <Label htmlFor={htmlFor} className="text-sm">
      {label} <span className="text-red-500" aria-hidden="true">*</span>
    </Label>
  );

  const renderError = (storedKey) => {
    const msg = errFor(storedKey);
    if (!msg) return null;
    return (
      <p
        className="mt-1 text-sm text-red-600"
        data-testid={`public-sd-${storedKey}-error`}
      >
        {msg}
      </p>
    );
  };

  const renderNumber = (k, label, placeholder, min = 0) => {
    const err = errFor(k);
    const inputId = `public-sd-${k}-input`;
    return (
      <div key={k} data-field={`sd.${k}`}>
        {renderLabel(label, inputId)}
        <Input
          id={inputId}
          type="number"
          inputMode="numeric"
          min={min}
          aria-invalid={err ? "true" : undefined}
          aria-describedby={err ? `public-sd-${k}-error` : undefined}
          // Always render the value as a string so React treats this as a
          // fully controlled input. Empty-string is allowed while editing
          // so the user can clear / retype freely; submit-time filtering
          // in PublicLeadForm strips empties before posting.
          value={details[k] === undefined || details[k] === null ? "" : String(details[k])}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              setKey(k, undefined);
            } else {
              // Keep digits only — strip stray characters from autofill /
              // paste. Number(raw) coerces "12" to 12 cleanly.
              const n = Number(raw);
              setKey(k, Number.isNaN(n) ? undefined : n);
            }
          }}
          placeholder={placeholder}
          className={cn(
            "mt-1.5 h-11",
            err && "border-red-500 focus-visible:ring-red-500"
          )}
          data-testid={`public-sd-${k}`}
        />
        {renderError(k)}
      </div>
    );
  };

  const renderYesNo = (k, label) => {
    const current =
      details[k] === true ? "yes" :
      details[k] === false ? "no" :
      "";
    const err = errFor(k);
    return (
      <div key={k} data-field={`sd.${k}`}>
        {renderLabel(label)}
        <Select value={current} onValueChange={(v) => setKey(k, v === "yes")}>
          <SelectTrigger
            className={cn(
              "mt-1.5 h-11",
              err && "border-red-500 focus:ring-red-500"
            )}
            aria-invalid={err ? "true" : undefined}
            data-testid={`public-sd-${k}`}
          >
            <SelectValue placeholder={t("public.form.selectPh")} />
          </SelectTrigger>
          <SelectContent>
            {YES_NO_OPTIONS.map((y) => (
              <SelectItem key={y} value={y}>
                {t(y === "yes" ? "public.form.yes" : "public.form.no")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {renderError(k)}
      </div>
    );
  };

  const renderEnum = (k, label, options, labelPrefix) => {
    const err = errFor(k);
    return (
      <div key={k} data-field={`sd.${k}`}>
        {renderLabel(label)}
        <Select value={details[k] || ""} onValueChange={(v) => setKey(k, v)}>
          <SelectTrigger
            className={cn(
              "mt-1.5 h-11",
              err && "border-red-500 focus:ring-red-500"
            )}
            aria-invalid={err ? "true" : undefined}
            data-testid={`public-sd-${k}`}
          >
            <SelectValue placeholder={t("public.form.selectPh")} />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o} value={o}>
                {t(`${labelPrefix}.${o}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {renderError(k)}
      </div>
    );
  };

  const cells = fields.map((k) => {
    switch (k) {
      case "bedrooms":
        return renderNumber("bedrooms", t("public.form.bedrooms"), "3");
      case "bathrooms":
        return renderNumber("bathrooms", t("public.form.bathrooms"), "2");
      case "num_bathrooms":
        // Bathroom-only sheet — write under the same `bathrooms` key
        // so downstream AI context sees one consistent field name.
        return renderNumber("bathrooms", t("public.form.numBathrooms"), "2");
      case "num_kitchens":
        return renderNumber("num_kitchens", t("public.form.numKitchens"), "1", 1);
      case "restrooms":
        return renderNumber("restrooms", t("public.form.numRestrooms"), "4");
      case "square_footage":
        return renderNumber(
          "square_footage",
          t("public.form.sqft"),
          t("public.form.sqftPh"),
          100
        );
      case "frequency":
        return renderEnum(
          "frequency",
          t("public.form.frequency"),
          serviceType === "Office / Commercial" ? COM_FREQ : RES_FREQ,
          "public.form.freq"
        );
      case "pets":
        return renderYesNo("pets", t("public.form.pets"));
      case "home_condition":
        return renderEnum(
          "home_condition",
          t("public.form.homeCondition"),
          CONDITION_OPTIONS,
          "public.form.cond"
        );
      case "property_empty":
        return renderYesNo("property_empty", t("public.form.propertyEmpty"));
      case "inside_appliances":
        return renderYesNo("inside_appliances", t("public.form.insideAppliances"));
      case "business_type":
        return renderEnum(
          "business_type",
          t("public.form.businessType"),
          BIZ_OPTIONS,
          "public.form.biz"
        );
      case "laundry_needed":
        return renderYesNo("laundry_needed", t("public.form.laundryNeeded"));
      case "restocking_needed":
        return renderYesNo("restocking_needed", t("public.form.restockingNeeded"));
      case "shower_tub":
        return renderYesNo("shower_tub", t("public.form.showerTub"));
      case "hard_water_grout":
        return renderYesNo("hard_water_grout", t("public.form.hardWater"));
      case "oven_inside":
        return renderYesNo("oven_inside", t("public.form.ovenInside"));
      case "fridge_inside":
        return renderYesNo("fridge_inside", t("public.form.fridgeInside"));
      case "cabinets_inside":
        return renderYesNo("cabinets_inside", t("public.form.cabinetsInside"));
      case "construction_type":
        return renderEnum(
          "construction_type",
          t("public.form.constructionType"),
          CONSTR_OPTIONS,
          "public.form.const"
        );
      case "heavy_dust":
        return renderYesNo("heavy_dust", t("public.form.heavyDust"));
      case "debris_removal":
        return renderYesNo("debris_removal", t("public.form.debrisRemoval"));
      default:
        return null;
    }
  });

  return (
    <div
      className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 sm:p-5"
      data-testid="public-service-details"
    >
      <p className="text-sm font-semibold text-gray-900">
        {t("public.form.smartHeader")}
      </p>
      <p className="mt-1 text-xs text-gray-500">{t("public.form.smartHint")}</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-3 sm:gap-4">{cells}</div>
    </div>
  );
}
